import {
  ErrorHandler,
  Injectable,
  isDevMode,
  OnDestroy
} from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { ErrorLog } from '../models/ErrorLog';
import { ErrorLogService } from './error-log.service';


@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler, OnDestroy {
  private subscriptions: Subscription[] = [];

  private errorSubject: Subject<ErrorLog> = new Subject()

  public constructor(private errorLogService: ErrorLogService) {
    this.subscriptions.push(
      this.errorSubject.pipe(distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)), debounceTime(500)).subscribe(e => {
        this.errorLogService.LogClientError(e);
      })
    );
  }

  public handleError(error: any): void {
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;

    if (chunkFailedMessage.test(error.message)) {
      window.location.reload();
    }

    if (
      error.name &&
      error.name === 'HttpErrorResponse' &&
      error.status === 500
    ) {
      // Error from the server is already logged. No need to throw it back to the backend.
      console.error('[Backend Error]');
      if (isDevMode()) {
        throw error;
      } else {
        return;
      }
    }

    const errorLog: ErrorLog = this.createClientErrorLog(error);
    try {
      errorLog.description =
        window.location.href + '  ---------  error:' + errorLog.description;
      errorLog.stacktrace = errorLog.stacktrace || "";

      if (errorLog.description.indexOf("Unable to log errorEvent") < 0 && errorLog.stacktrace.indexOf("Unable to log errorEvent") < 0 && errorLog.fileName.indexOf("Unable to log errorEvent") < 0) {
        this.errorSubject.next(errorLog);
      }

    } catch (e) {
      console.error('failed to log client error', errorLog, e);
    } finally {
      if (isDevMode()) {
        throw error;
      } else {
        console.log('Progreso error', error);
      }
    }
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  private createClientErrorLog(error: any): ErrorLog {
    const description: string = error.message ? error.message : "";
    const stacktrace: string = error.stack ? error.stack : "";
    const loginKey: string = localStorage.getItem('Progreso.loginKey');
    const securityCode: string = localStorage.getItem('Progreso.securityCode');
    const fileName: string = this.extractFileName(error);
    const source: string = this.extractSource(error);
    return {
      errorId: 0,
      description,
      fileName,
      source,
      stacktrace,
      loginKey,
      securityCode,
    };
  }

  private extractFileName(error: any): string {
    const browser: BrowserType = this.browserDetection();
    switch (browser) {
      case BrowserType.Chrome:
        return this.chromeFileExtract(error);
      case BrowserType.Firefox:
        return this.firefoxFileExtract(error);
      case BrowserType.InternetExplorer:
        return this.internetExplorerFileExtract(error);
      case BrowserType.Edge:
        return this.internetExplorerFileExtract(error);
      default:
        return 'unknown';
    }
  }

  private extractSource(error: any): string {
    const browser: BrowserType = this.browserDetection();
    switch (browser) {
      case BrowserType.Chrome:
        return this.chromeSourceExtract(error);
      case BrowserType.Firefox:
        return this.firefoxSourceExtract(error);
      case BrowserType.InternetExplorer:
        return this.internetExplorerSourceExtract(error);
      case BrowserType.Edge:
        return this.internetExplorerSourceExtract(error);
      default:
        return 'unknown';
    }
  }

  private chromeFileExtract(error: any): string {
    if (error && error.stack) {
      const frames: string[] = error.stack.split('\n');
      const segments: string[] = frames[1].split('(')[0].split('/');
      const endOfFileName: number =
        segments[segments.length - 1].search('.ts') + 3;
      let fileName: string;
      // for production mode where there is no .ts file in the error log.
      if (endOfFileName < 3) {
        const fileNameSegments: string[] =
          segments[segments.length - 1].split('at');
        const fileNameWithMethod: string[] = fileNameSegments[
          fileNameSegments.length - 1
        ]
          .trim()
          .split('.');
        fileName = fileNameWithMethod.reduce(
          (previous: string, current: string, index: number) => {
            if (index === fileNameWithMethod.length - 1) {
              return previous;
            }
            return `${previous}.${current}`;
          }
        );
      } else {
        fileName = segments[segments.length - 1].substring(0, endOfFileName);
      }
      if (fileName.trim().length !== 0) {
        return fileName;
      }
    }
    return 'unknown';
  }

  private internetExplorerFileExtract(error: any): string {
    const frames: string[] = error.stack?.split('\n') ?? [];
    let fileName = '';
    const fileNameWithPrototype = frames[1].trim().split(' ')[1].split('.')[0];
    const fileNameSegments: string[] = fileNameWithPrototype.split(/(?=[A-Z])/);
    fileName = fileNameSegments.reduce(
      (previous: string, current: string, index: number) => {
        if (index === fileNameSegments.length - 1) {
          return `${previous}.${current}.ts`;
        }
        return `${previous}-${current}`;
      }
    );
    if (fileName.trim().length !== 0) {
      return fileName.toLowerCase();
    }
    return 'unknown';
  }

  private firefoxFileExtract(error: any): string {
    if (error.stack) {
      try {
        const frames: string[] = error.stack?.split('\n') ?? [];
        const frameSegments: string[] = frames[0]?.split('@')[0]?.split('/') ?? [];
        const fileNameWithPrototype: string =
          frameSegments[frameSegments.length - 1].split('.')[0];
        const fileNameSegments: string[] = fileNameWithPrototype.split(/(?=[A-Z])/);
        const fileName: string = fileNameSegments.reduce(
          (previous: string, current: string, index: number) => {
            if (index === fileNameSegments.length - 1) {
              return `${previous}.${current}.ts`;
            }
            return `${previous}-${current}`;
          }
        );
        if (fileName.trim().length !== 0) {
          return fileName.toLowerCase();
        }
      } catch (e) {
        return 'unknown';
      }
    }
    return 'unknown';
  }

  private chromeSourceExtract(error: any): string {
    if (error.stack) {
      try {
        const frames: string[] = error.stack.split('\n');
        const segments: string[] = frames[1].split('(')[0].split('/');
        const endOfFileName: number =
          segments[segments.length - 1].search('.ts') + 3;
        const source: string = segments[segments.length - 1]
          .substring(endOfFileName + 1)
          .split('.')[1];
        if (source.trim().length !== 0) {
          return source;
        }
      } catch (e) {
        return 'unknown';
      }
    }
    return 'unknown';
  }

  private internetExplorerSourceExtract(error: any): string {
    if (error.stack) {
      const frames: string[] = error.stack.split('\n');
      const segments: string[] = frames[1].split('(')[0].split('.');
      const source = segments[segments.length - 1];
      if (source.trim().length !== 0) {
        return source;
      }
    }
    return 'unknown';
  }

  private firefoxSourceExtract(error: any): string {
    if (error.stack) {
      const frames: string[] = error.stack?.split('\n') ?? [];
      const segments: string[] = frames[0].split('@')[0].split('.');
      const source: string = segments[segments.length - 1];
      if (source.trim().length !== 0) {
        return source;
      }
    }
    return 'unknown';
  }

  private browserDetection(): BrowserType {
    const windowStub: any = window;
    const documentStub: any = document;
    // Opera 8.0+
    if (
      !!windowStub.opr ||
      !!windowStub.opera ||
      navigator.userAgent.indexOf(' OPR/') >= 0
    ) {
      return BrowserType.Opera;
    }

    // Firefox 1.0+
    if ('InstallTrigger' in windowStub) {
      return BrowserType.Firefox;
    }

    // Safari 3.0+ "[object HTMLElementConstructor]"
    if (
      /constructor/i.test(windowStub.HTMLElement) ||
      (function (p) {
        return p.toString() === '[object SafariRemoteNotification]';
      })(!window['safari'])
    ) {
      return BrowserType.Safari;
    }

    // Internet Explorer 6-11
    if (/*@cc_on!@*/ false || !!documentStub.documentMode) {
      return BrowserType.InternetExplorer;
    }

    // Edge 20+
    if (!!windowStub.StyleMedia) {
      return BrowserType.Edge;
    }

    // Chrome 1 - 71
    if (
      !!windowStub.chrome &&
      (!!windowStub.chrome.webstore || !!windowStub.chrome.runtime)
    ) {
      return BrowserType.Chrome;
    }

    // Blink engine detection
    if (!!windowStub.CSS) {
      return BrowserType.Blink;
    }
  }
}

enum BrowserType {
  Firefox,
  Chrome,
  Safari,
  Opera,
  InternetExplorer,
  Edge,
  Blink,
  Undefined,
}